import { FiStar } from 'react-icons/fi';

export const wishClausesData = {
  courseId: 'wish-clauses',
  courseType: 'grammar',
  title: 'Wish & If Only Clauses',
  icon: FiStar,
  overview: {
    impact: 'Express regrets and desires about present and past',
    focus: 'wish/if only + past simple/past perfect',
    uses: 'Speaking Parts 2-3',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Wishes About the Present',
      content: `<p><strong>I wish / If only + past simple</strong> (for things you want to be different NOW)</p>`,
      examples: [
        {
          label: 'Wish',
          text: 'I wish I had more free time. (But I don\'t.)'
        },
        {
          label: 'If only',
          text: 'If only I spoke French. (But I don\'t.)'
        }
      ],
      warning: 'Use \'were\' not \'was\' in formal English: \'I wish I were taller.\''
    },
    {
      title: 'Wishes About the Past',
      content: `<p><strong>I wish / If only + past perfect</strong> (for regrets about the PAST)</p>`,
      examples: [
        {
          label: 'Regret',
          text: 'I wish I had studied harder at school.'
        },
        {
          label: 'If only',
          text: 'If only I hadn\'t said that.'
        }
      ]
    },
    {
      title: 'Wishes About Annoying Habits',
      content: `<p><strong>I wish + would</strong> (for things you want others to change)</p>`,
      examples: [
        {
          label: 'Complaint',
          text: 'I wish you would stop making so much noise.'
        },
        {
          label: 'Desire',
          text: 'I wish it would stop raining.'
        }
      ],
      warning: 'Never say \'I wish I would...\' — use this only for OTHER people/things.'
    }
  ],
  exercises: [
    {
      title: 'Choose the Correct Form',
      type: 'select',
      instructions: 'Select the right wish structure.',
      questions: [
        {
          text: 'I wish I ___ taller. (present wish)',
          options: [
            'were',
            'am',
            'had been'
          ],
          answer: 'were',
          explanation: 'Present wish → past simple. Formal: \'were\' for all subjects.'
        },
        {
          text: 'I wish I ___ to the party last night. (past regret)',
          options: [
            'had gone',
            'went',
            'would go'
          ],
          answer: 'had gone',
          explanation: 'Past regret → past perfect.'
        },
        {
          text: 'I wish he ___ so loudly. (annoying habit)',
          options: [
            'wouldn\'t talk',
            'didn\'t talk',
            'hadn\'t talked'
          ],
          answer: 'wouldn\'t talk',
          explanation: 'Annoying habit of another person → wish + would.'
        }
      ]
    }
  ],
  tips: [
    'Present wishes: wish + past simple',
    'Past regrets: wish + past perfect',
    'Annoying habits: wish + would (only for others)',
    '\'If only\' = stronger version of \'wish\''
  ],
  dailyPractice: [
    'Practice wish & if only clauses concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Wish & If Only: Past simple for present wishes, past perfect for past regrets, would for annoying habits. Useful for IELTS Speaking about changes and regrets.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...wishClausesData,
      title: wishClausesData.title + ' (Foundations)',
      overview: { ...wishClausesData.overview, duration: '15-20 minutes' },
      sections: wishClausesData.sections.slice(0, Math.max(2, Math.ceil(wishClausesData.sections.length / 2))),
      exercises: wishClausesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...wishClausesData,
      title: wishClausesData.title + ' (Advanced)',
      overview: { ...wishClausesData.overview, duration: '30-35 minutes' },
      sections: wishClausesData.sections,
      exercises: wishClausesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(wishClausesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return wishClausesData;
}
